// PROPOSITO: el menu del dia y lo que la alumna marco que comio.
// DEPENDENCIAS: src/lib/supabase, src/types/dominio.
// NO OBVIO: las opciones de un mismo momento vienen ya igualadas en calorias por la entrenadora
// (modelo de equivalentes de Nutrium). Al intercambiar un plato NO se recalcula ningun gramaje:
// ningun producto verificado del sector lo hace, y prometerlo seria inventar un patron.

import { supabase } from '@/lib/supabase'
import { INTERRUPTORES } from '@/config/constantes'
import { menuDeDemostracion } from '@/services/demo/fixtures.comida'
import type { ComidaDelDia, MomentoComida, OpcionDeMenu } from '@/types/dominio'

interface FilaOpcion {
  id: string
  momento: MomentoComida
  orden: number
  recetas: {
    id: string
    nombre: string
    kcal: number
    proteina_g: number
    carbohidrato_g: number
    grasa_g: number
    ingredientes: string
    preparacion: string
    etiquetas: string[]
  }
}

const ORDEN_DEL_DIA: MomentoComida[] = [
  'desayuno', 'media_manana', 'almuerzo', 'merienda', 'cena', 'colacion',
]

export async function traerMenuDelDia(alumnaId: string, fecha: string): Promise<ComidaDelDia[]> {
  if (INTERRUPTORES.demo) return menuDeDemostracion()

  const { data: menu } = await supabase
    .from('menus')
    .select('id')
    .eq('alumna_id', alumnaId)
    .not('publicado_at', 'is', null)
    .lte('fecha_inicio', fecha)
    .order('fecha_inicio', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (!menu) return []

  const [opciones, marcadas] = await Promise.all([
    supabase
      .from('menu_opciones')
      .select(
        'id, momento, orden, recetas (id, nombre, kcal, proteina_g, carbohidrato_g, grasa_g, ingredientes, preparacion, etiquetas)',
      )
      .eq('menu_id', menu.id)
      .order('orden'),
    supabase
      .from('registros_comida')
      .select('momento, menu_opcion_id, fuera_del_plan')
      .eq('alumna_id', alumnaId)
      .eq('fecha', fecha),
  ])

  const porMomento = new Map<MomentoComida, OpcionDeMenu[]>()
  for (const fila of (opciones.data ?? []) as unknown as FilaOpcion[]) {
    const lista = porMomento.get(fila.momento) ?? []
    lista.push({
      id: fila.id,
      recetaId: fila.recetas.id,
      nombre: fila.recetas.nombre,
      momento: fila.momento,
      kcal: fila.recetas.kcal,
      proteinaG: fila.recetas.proteina_g,
      carbohidratoG: fila.recetas.carbohidrato_g,
      grasaG: fila.recetas.grasa_g,
      ingredientes: fila.recetas.ingredientes,
      preparacion: fila.recetas.preparacion,
      etiquetas: fila.recetas.etiquetas,
    })
    porMomento.set(fila.momento, lista)
  }

  return ORDEN_DEL_DIA.filter(m => porMomento.has(m)).map(momento => {
    const marcada = (marcadas.data ?? []).find(r => r.momento === momento)
    return {
      momento,
      opciones: porMomento.get(momento) ?? [],
      elegidaId: marcada?.menu_opcion_id ?? null,
      fueraDelPlan: marcada?.fuera_del_plan ?? null,
    }
  })
}

export interface ComidaAMarcar {
  clienteId: string
  alumnaId: string
  fecha: string
  momento: MomentoComida
  menuOpcionId: string | null
  fueraDelPlan: string | null
}

export async function marcarComida(comida: ComidaAMarcar): Promise<void> {
  if (INTERRUPTORES.demo) return

  const { error } = await supabase.from('registros_comida').upsert(
    {
      cliente_id: comida.clienteId,
      alumna_id: comida.alumnaId,
      fecha: comida.fecha,
      momento: comida.momento,
      menu_opcion_id: comida.menuOpcionId,
      fuera_del_plan: comida.fueraDelPlan,
    },
    { onConflict: 'cliente_id' },
  )
  if (error) throw error
}
